import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import type {
  VisitorConfig,
  Lead,
  Supplier,
  SupplierOffer,
  AppNotification,
  AccountingEntry,
  SitePage,
  SitePageSummary,
  BlogPost,
  BlogPostSummary,
} from './types';
import { initialVisitorConfig, withConfigDefaults } from './data';
import { api, ApiError, type CurrentUser } from './api/client';
import LoginScreen from './components/LoginScreen';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import ImpersonationBanner from './components/ImpersonationBanner';
import { PageRenderer } from './components/site/PageRenderer';
import { BlogIndex } from './components/site/BlogIndex';
import { BlogArticle } from './components/site/BlogArticle';
import { useFavicon } from './utils/useFavicon';

const VisitorPortal = lazy(() => import('./components/VisitorPortal'));
const BackOfficeAdmin = lazy(() => import('./components/BackOfficeAdmin'));
const BackOfficeClient = lazy(() => import('./components/BackOfficeClient'));
const BackOfficeSupplier = lazy(() => import('./components/BackOfficeSupplier'));
const GuestFeedContainer = lazy(() => import('./components/GuestFeedContainer'));
const SupplierApplicationPage = lazy(() => import('./components/SupplierApplicationPage'));

const SUPPLIER_APPLY_PATH = 'devenir-prestataire';

function LoadingView({ label = "Chargement…" }: { label?: string }) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-400">
      <div className="w-8 h-8 border-2 border-slate-200 border-t-[#D95D39] rounded-full animate-spin" />
      <span className="text-xs font-bold uppercase tracking-widest">{label}</span>
    </div>
  );
}

/**
 * Vue courante, déduite de l'URL.
 *  - ?e=<jeton>  : mur convives, sans compte (lien du QR code)
 *  - ?espace     : espace professionnel (connexion puis back-office du rôle)
 *  - /<slug>     : page créée avec l'éditeur de pages
 *  - défaut      : site vitrine public (accueil)
 */
type View = 'site' | 'workspace' | 'guest' | 'page' | 'supplier-apply' | 'blog' | 'blog-post';

/**
 * Chemins que l'éditeur de pages ne doit jamais pouvoir revendiquer : ils sont
 * déjà servis par le serveur (server/app.ts) avant même d'atteindre l'index.html
 * de l'application, ou par une route dédiée (formulaire "devenir prestataire").
 * Tenue identique à la liste réservée côté serveur (server/pageSections.ts).
 */
const BLOG_PATH = 'blog';

const RESERVED_PATH_SEGMENTS = new Set([
  'api',
  'media',
  'healthz',
  'robots.txt',
  'internal',
  SUPPLIER_APPLY_PATH,
  BLOG_PATH,
]);

function readView(): { view: View; shareToken: string; pageSlug: string } {
  const params = new URLSearchParams(window.location.search);
  const shareToken = params.get('e') || '';
  if (shareToken) return { view: 'guest', shareToken, pageSlug: '' };
  if (params.has('espace')) return { view: 'workspace', shareToken: '', pageSlug: '' };

  const path = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (path === SUPPLIER_APPLY_PATH) {
    return { view: 'supplier-apply', shareToken: '', pageSlug: '' };
  }
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === BLOG_PATH) {
    return segments.length > 1
      ? { view: 'blog-post', shareToken: '', pageSlug: segments[1] }
      : { view: 'blog', shareToken: '', pageSlug: '' };
  }
  if (path && !RESERVED_PATH_SEGMENTS.has(segments[0])) {
    return { view: 'page', shareToken: '', pageSlug: path };
  }
  return { view: 'site', shareToken: '', pageSlug: '' };
}

export default function App() {
  const [{ view, shareToken, pageSlug }, setLocation] = useState(readView);

  const navigate = useCallback((next: View) => {
    const url = next === 'workspace' ? '/?espace' : '/';
    window.history.pushState({}, '', url);
    setLocation(readView());
  }, []);

  // Boutons précédent/suivant du navigateur.
  useEffect(() => {
    const onPop = () => setLocation(readView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (view === 'guest') {
    return (
      <Suspense fallback={<LoadingView label="Ouverture du mur…" />}>
        <GuestFeedContainer shareToken={shareToken} />
      </Suspense>
    );
  }

  if (view === 'workspace') {
    return <Workspace onBackToSite={() => navigate('site')} />;
  }

  if (view === 'page') {
    return <CustomPageView slug={pageSlug} />;
  }

  if (view === 'blog') {
    return <BlogIndexView />;
  }

  if (view === 'blog-post') {
    return <BlogPostView slug={pageSlug} />;
  }

  if (view === 'supplier-apply') {
    return (
      <Suspense fallback={<LoadingView />}>
        <SupplierApplicationPage />
      </Suspense>
    );
  }

  return <PublicSite onOpenWorkspace={() => navigate('workspace')} />;
}

// ---------------------------------------------------------------------------
// Page créée avec l'éditeur de pages (hors accueil)
// ---------------------------------------------------------------------------

function NotFoundPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-4">
      <h1 className="text-2xl font-bold">Page introuvable</h1>
      <p className="text-gray-500">Cette page n'existe pas ou plus.</p>
      <a href="/" className="text-sm font-semibold underline">
        Retour à l'accueil
      </a>
    </div>
  );
}

function CustomPageView({ slug }: { slug: string }) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'not-found' }
    | { status: 'ready'; config: VisitorConfig; page: SitePage; menuPages: SitePageSummary[] }
  >({ status: 'loading' });

  useFavicon(state.status === 'ready' ? state.config.faviconUrl : undefined);

  useEffect(() => {
    setState({ status: 'loading' });
    Promise.all([api.publicConfig(), api.getPageBySlug(slug)])
      .then(([configRes, pageRes]) => {
        setState({
          status: 'ready',
          config: withConfigDefaults(configRes.config),
          page: pageRes.page,
          menuPages: configRes.menuPages,
        });
      })
      .catch(() => setState({ status: 'not-found' }));
  }, [slug]);

  if (state.status === 'loading') return <LoadingView label="Chargement de la page…" />;
  if (state.status === 'not-found') return <NotFoundPage />;

  return <PageRenderer page={state.page} config={state.config} menuPages={state.menuPages} />;
}

// ---------------------------------------------------------------------------
// Blog de l'agence
// ---------------------------------------------------------------------------

function BlogIndexView() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; config: VisitorConfig; posts: BlogPostSummary[]; menuPages: SitePageSummary[] }
  >({ status: 'loading' });

  useFavicon(state.status === 'ready' ? state.config.faviconUrl : undefined);

  useEffect(() => {
    Promise.all([api.publicConfig(), api.listBlogPosts()])
      .then(([configRes, blogRes]) => {
        setState({
          status: 'ready',
          config: withConfigDefaults(configRes.config),
          posts: blogRes.posts,
          menuPages: configRes.menuPages,
        });
      })
      .catch(() => setState({ status: 'error' }));
  }, []);

  if (state.status === 'loading') return <LoadingView label="Chargement du blog…" />;
  if (state.status === 'error') return <NotFoundPage />;

  return <BlogIndex config={state.config} posts={state.posts} menuPages={state.menuPages} />;
}

function BlogPostView({ slug }: { slug: string }) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'not-found' }
    | {
        status: 'ready';
        config: VisitorConfig;
        post: BlogPost;
        menuPages: SitePageSummary[];
        siteUrl: string;
      }
  >({ status: 'loading' });

  useFavicon(state.status === 'ready' ? state.config.faviconUrl : undefined);

  useEffect(() => {
    setState({ status: 'loading' });
    Promise.all([api.publicConfig(), api.getBlogPostBySlug(slug)])
      .then(([configRes, postRes]) => {
        setState({
          status: 'ready',
          config: withConfigDefaults(configRes.config),
          post: postRes.post,
          menuPages: configRes.menuPages,
          siteUrl: configRes.siteUrl,
        });
      })
      .catch(() => setState({ status: 'not-found' }));
  }, [slug]);

  if (state.status === 'loading') return <LoadingView label="Chargement de l'article…" />;
  if (state.status === 'not-found') return <NotFoundPage />;

  return (
    <BlogArticle config={state.config} post={state.post} menuPages={state.menuPages} siteUrl={state.siteUrl} />
  );
}

// ---------------------------------------------------------------------------
// Site vitrine public
// ---------------------------------------------------------------------------

function PublicSite({ onOpenWorkspace }: { onOpenWorkspace: () => void }) {
  const [config, setConfig] = useState<VisitorConfig | null>(null);
  const [homePage, setHomePage] = useState<SitePage | null>(null);
  const [menuPages, setMenuPages] = useState<SitePageSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Clé publique hCaptcha de l'agence. Vide quand la vérification n'est pas configurée. */
  const [captchaSiteKey, setCaptchaSiteKey] = useState('');

  useFavicon(config?.faviconUrl);

  useEffect(() => {
    api
      .publicConfig()
      .then((res) => {
        setConfig(withConfigDefaults(res.config));
        setCaptchaSiteKey(res.captcha?.enabled ? res.captcha.siteKey : '');
        setHomePage(res.homePage);
        setMenuPages(res.menuPages);
      })
      .catch(() => {
        // Le site vitrine doit rester consultable même si l'API est indisponible :
        // c'est la vitrine commerciale, pas un outil interne.
        setConfig(initialVisitorConfig);
        setLoadError("Contenu par défaut affiché : la configuration n'a pas pu être chargée.");
      });
  }, []);

  const handleSubmitQuote = useCallback(async (payload: Parameters<typeof api.submitQuote>[0]) => {
    const { lead } = await api.submitQuote(payload);
    return lead;
  }, []);

  if (!config) return <LoadingView label="Chargement du site…" />;

  // Tant que l'agence n'a ajouté aucune section à sa page d'accueil, l'ancien
  // rendu figé (VisitorPortal) reste affiché à l'identique : aucun site en
  // production ne change d'apparence du seul fait de cette fonctionnalité.
  if (homePage && homePage.sections.length > 0) {
    return <PageRenderer page={homePage} config={config} menuPages={menuPages} />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#FDFCFB] text-[#141414] font-sans antialiased">
      {loadError && (
        <div className="bg-amber-50 border-b border-amber-300 text-amber-900 px-4 py-2 text-[11px] font-bold text-center">
          {loadError}
        </div>
      )}

      <Suspense fallback={<LoadingView />}>
        <VisitorPortal
          config={config}
          onSubmitQuote={handleSubmitQuote}
          onOpenWorkspace={onOpenWorkspace}
          captchaSiteKey={captchaSiteKey}
          menuPages={menuPages}
        />
      </Suspense>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Espace professionnel : connexion puis back-office correspondant au compte
// ---------------------------------------------------------------------------

function Workspace({ onBackToSite }: { onBackToSite: () => void }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [checking, setChecking] = useState(true);

  const [config, setConfig] = useState<VisitorConfig>(initialVisitorConfig);
  useFavicon(config.faviconUrl);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierOffers, setSupplierOffers] = useState<SupplierOffer[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [accountingEntries, setAccountingEntries] = useState<AccountingEntry[]>([]);

  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  const loadAll = useCallback(async () => {
    const data = await api.bootstrap();
    setUser(data.user);
    setConfig(withConfigDefaults(data.config));
    setLeads(data.leads);
    setSuppliers(data.suppliers);
    setSupplierOffers(data.supplierOffers);
    setNotifications(data.notifications);
    setAccountingEntries(data.accountingEntries);
    setSelectedLeadId((current) =>
      current && data.leads.some((l) => l.id === current) ? current : data.leads[0]?.id || ''
    );
  }, []);

  useEffect(() => {
    loadAll()
      .catch((err) => {
        // 401 = simplement pas connecté ; tout le reste est une vraie panne.
        if (!(err instanceof ApiError && err.status === 401)) {
          setError(err instanceof ApiError ? err.message : 'Chargement impossible.');
        }
      })
      .finally(() => setChecking(false));
  }, [loadAll]);

  // -------------------------------------------------------------------------
  // Mutations : l'API fait foi, l'état local est resynchronisé sur sa réponse.
  // -------------------------------------------------------------------------

  const reportError = (err: unknown, fallback: string) => {
    setError(err instanceof ApiError ? err.message : fallback);
    setTimeout(() => setError(null), 6000);
  };

  const handleUpdateLead = useCallback(async (leadId: string, fields: Partial<Lead>) => {
    try {
      const { lead } = await api.updateLead(leadId, fields);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...lead } : l)));
    } catch (err) {
      reportError(err, 'Enregistrement du dossier impossible.');
    }
  }, []);

  const handleCreateLead = useCallback(async (payload: Parameters<typeof api.createLead>[0]) => {
    const { lead } = await api.createLead(payload);
    setLeads((prev) => [lead, ...prev]);
    return lead;
  }, []);

  const handleAddSupplier = useCallback(async (supplier: Omit<Supplier, 'id'>) => {
    try {
      const { supplier: created } = await api.createSupplier(supplier);
      setSuppliers((prev) => [...prev, created]);
    } catch (err) {
      reportError(err, 'Création du fournisseur impossible.');
    }
  }, []);

  const handleUpdateSupplier = useCallback(async (id: string, fields: Partial<Supplier>) => {
    try {
      const { supplier } = await api.updateSupplier(id, fields);
      setSuppliers((prev) => prev.map((s) => (s.id === supplier.id ? supplier : s)));
    } catch (err) {
      reportError(err, 'Mise à jour du fournisseur impossible.');
    }
  }, []);

  const handleAddSupplierOffer = useCallback(
    async (offer: Omit<SupplierOffer, 'id'>) => {
      try {
        const { offer: created } = await api.createOffer(offer);
        setSupplierOffers((prev) => [created, ...prev]);
        // L'offre déclenche une notification côté serveur : on recharge pour
        // que l'administrateur la voie sans rafraîchir la page.
        await loadAll().catch(() => undefined);
      } catch (err) {
        reportError(err, "Dépôt de l'offre impossible.");
      }
    },
    [loadAll]
  );

  const handleAddSupplierOffersBulk = useCallback(
    async (payload: { leadId: string; supplierIds: string[]; amount: number; description: string }) => {
      try {
        const { offers: created } = await api.createOffersBulk(payload);
        setSupplierOffers((prev) => [...created, ...prev]);
        await loadAll().catch(() => undefined);
        return created;
      } catch (err) {
        reportError(err, 'Dépôt groupé des offres impossible.');
        return [];
      }
    },
    [loadAll]
  );

  const handleUpdateSupplierOffer = useCallback(
    async (id: string, fields: Partial<SupplierOffer>) => {
      try {
        const { offer } = await api.updateOffer(id, fields);
        setSupplierOffers((prev) => prev.map((o) => (o.id === offer.id ? offer : o)));
        await loadAll().catch(() => undefined);
      } catch (err) {
        reportError(err, "Mise à jour de l'offre impossible.");
      }
    },
    [loadAll]
  );

  const handleUpdateConfig = useCallback(async (next: VisitorConfig) => {
    setConfig(next);
    try {
      await api.saveConfig(next);
    } catch (err) {
      reportError(err, 'Enregistrement de la configuration impossible.');
    }
  }, []);

  const handleAddAccountingEntry = useCallback(
    async (entry: Omit<AccountingEntry, 'id' | 'createdAt'>) => {
      try {
        const { entry: created } = await api.createAccountingEntry(entry);
        setAccountingEntries((prev) => [created, ...prev]);
      } catch (err) {
        reportError(err, "Enregistrement de l'écriture impossible.");
      }
    },
    []
  );

  const handleDeleteAccountingEntry = useCallback(async (id: string) => {
    try {
      await api.deleteAccountingEntry(id);
      setAccountingEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      reportError(err, "Suppression de l'écriture impossible.");
    }
  }, []);

  const handleMarkNotificationRead = useCallback(async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    await api.markNotificationRead(id).catch(() => undefined);
  }, []);

  const handleMarkAllNotificationsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await api.markAllNotificationsRead().catch(() => undefined);
  }, []);

  const handleClearNotifications = useCallback(async () => {
    setNotifications([]);
    await api.clearNotifications().catch(() => undefined);
  }, []);

  /** Ouvre le mur convives dans un onglet, via le jeton de partage du dossier. */
  const handleOpenGuestMediaFeed = useCallback(
    (leadId: string) => {
      const lead = leads.find((l) => l.id === leadId);
      if (!lead?.guestShareToken) return;
      window.open(`/?e=${encodeURIComponent(lead.guestShareToken)}`, '_blank', 'noopener,noreferrer');
    },
    [leads]
  );

  const handleLogout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
    // Rechargement complet plutôt que retour interne au SPA : la page d'accueil
    // du locataire « feed-it » est une page produit servie par le serveur
    // (server.ts), et non une vue de l'application. Une navigation interne
    // afficherait le site vitrine de l'éditeur à sa place. Le rechargement
    // repart aussi d'un état vierge, sans donnée de la session fermée.
    window.location.assign('/');
  }, []);

  // -------------------------------------------------------------------------

  if (checking) return <LoadingView label="Vérification de la session…" />;

  if (!user) {
    return (
      <LoginScreen
        onAuthenticated={() => {
          setChecking(true);
          loadAll()
            .catch((err) => reportError(err, 'Chargement impossible.'))
            .finally(() => setChecking(false));
        }}
        onBackToSite={onBackToSite}
        brandColor={config.brandColor}
      />
    );
  }

  const selectedLead = leads.find((l) => l.id === selectedLeadId) || leads[0];

  return (
    <div className="flex flex-col min-h-screen bg-[#FDFCFB] text-[#141414] font-sans antialiased">
      {/* Prise en main de compte : le bandeau est permanent et vient avant
          tout le reste. Sans lui, un exploitant venu dépanner prend la vue de
          l'agence pour la sienne et agit sur ses données en croyant être chez
          lui. */}
      <ImpersonationBanner user={user} />

      <WorkspaceHeader
        user={user}
        brandColor={config.brandColor}
        unreadCount={notifications.filter((n) => !n.read).length}
        onLogout={handleLogout}
        onBackToSite={onBackToSite}
        onChangePassword={() => setShowPasswordDialog(true)}
      />

      {showPasswordDialog && (
        <ChangePasswordDialog
          onClose={() => setShowPasswordDialog(false)}
          brandColor={config.brandColor}
        />
      )}

      {error && (
        <div role="alert" className="bg-rose-50 border-b border-rose-300 text-rose-900 px-4 py-2.5 text-xs font-bold text-center">
          {error}
        </div>
      )}

      <div className="flex-1">
        <Suspense fallback={<LoadingView />}>
          {user.role === 'admin' && (
            <BackOfficeAdmin
              config={config}
              onUpdateConfig={handleUpdateConfig}
              leads={leads}
              onUpdateLead={handleUpdateLead}
              onCreateLead={handleCreateLead}
              suppliers={suppliers}
              onAddSupplier={handleAddSupplier}
              onUpdateSupplier={handleUpdateSupplier}
              supplierOffers={supplierOffers}
              onAddSupplierOffer={handleAddSupplierOffer}
              onAddSupplierOffersBulk={handleAddSupplierOffersBulk}
              onUpdateSupplierOffer={handleUpdateSupplierOffer}
              notifications={notifications}
              onMarkNotificationRead={handleMarkNotificationRead}
              onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
              onClearNotifications={handleClearNotifications}
              accountingEntries={accountingEntries}
              onAddAccountingEntry={handleAddAccountingEntry}
              onDeleteAccountingEntry={handleDeleteAccountingEntry}
              onOpenGuestMediaFeed={handleOpenGuestMediaFeed}
              currentUserId={user.id}
              isPlatformAdmin={user.isPlatformAdmin}
            />
          )}

          {user.role === 'client' && selectedLead && (
            <BackOfficeClient
              leads={leads}
              selectedLeadId={selectedLead.id}
              onSelectLead={setSelectedLeadId}
              onUpdateLead={handleUpdateLead}
              onOpenGuestMediaFeed={handleOpenGuestMediaFeed}
            />
          )}

          {user.role === 'supplier' && (
            <BackOfficeSupplier
              suppliers={suppliers}
              selectedSupplierId={user.supplierId || suppliers[0]?.id || ''}
              onSelectSupplier={() => undefined}
              supplierOffers={supplierOffers}
              onAddSupplierOffer={handleAddSupplierOffer}
              onUpdateSupplierOffer={handleUpdateSupplierOffer}
              leads={leads}
              onUpdateLead={handleUpdateLead}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
}

function WorkspaceHeader({
  user,
  brandColor = '#D95D39',
  unreadCount,
  onLogout,
  onBackToSite,
  onChangePassword,
}: {
  user: CurrentUser;
  brandColor?: string;
  unreadCount: number;
  onLogout: () => void;
  onBackToSite: () => void;
  onChangePassword: () => void;
}) {
  const roleLabel =
    user.role === 'admin'
      ? 'Administration'
      : user.role === 'client'
        ? 'Espace client'
        : 'Espace partenaire';

  return (
    <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3 sticky top-0 z-50">
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className="text-white text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded"
          style={{ backgroundColor: brandColor }}
        >
          feed-it
        </span>
        <span className="text-xs font-bold text-slate-700 truncate">{roleLabel}</span>
        {unreadCount > 0 && (
          <span className="bg-rose-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">
            {unreadCount}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-slate-500 font-semibold hidden sm:inline truncate max-w-[180px]">
          {user.email}
        </span>
        <button
          onClick={onChangePassword}
          className="px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg cursor-pointer transition-colors"
        >
          Mot de passe
        </button>
        <button
          onClick={onBackToSite}
          className="px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg cursor-pointer transition-colors"
        >
          Voir le site
        </button>
        <button
          onClick={onLogout}
          className="px-2.5 py-1.5 text-[11px] font-bold text-white rounded-lg cursor-pointer transition-opacity hover:opacity-90"
          style={{ backgroundColor: brandColor }}
        >
          Déconnexion
        </button>
      </div>
    </header>
  );
}
